import { analytics } from '@qbe/qal';
import { AnyAction } from '@reduxjs/toolkit';

import type { RootState } from '../../store';
import {
  analyticsConstant,
  AnalyticsStandard,
  AnalyticsStandardEvents,
  GoalEvents,
} from '../../types/analytics';
import { apiMappings } from './apiEndpoints';
import mapStore from './mapStore';
import { getApiName, getApiStatus } from './trackUtils';
import { GoalEvent } from './types-only';

/**
 * Tracks the start of an API transaction, capturing relevant details such as API name, request ID,
 * and additional data from the application state. This function is designed to be called when an API request begins.
 *
 * @param {string} type - The type or category of the API transaction.
 * @param {object} Meta - The Meta Obejct.
 * @param {RootState} state - The application state at the time of the API transaction start.
 */

export const qalTrackApiStart = (type: string, action: AnyAction, state: RootState): void => {
  const { requestId, arg } = action.meta || {};
  if (!requestId) return;

  const { endpointName } = arg;
  const apiType = getApiName(type);
  const apiName = `${apiType}/${endpointName}`;

  const key = `${apiType}:${endpointName}`;
  let apiEndpoint: string;

  if (typeof apiMappings[key] === 'function') {
    apiEndpoint = (apiMappings[key] as (args?: Record<string, unknown>) => string)(
      arg.originalArgs
    );
  } else {
    apiEndpoint = (apiMappings[key] as string) || '';
  }

  analytics.trackApiStart({
    eventAction: apiName,
    eventLabel: getApiStatus(type),
    data: {
      apiEndpoint,
      apiName,
      requestId,
      ...mapStore(state),
    },
  });
};

/**
 * Tracks the successful completion of an API transaction for analytics purposes.
 *
 * This function is called when an API response is received, capturing key information about
 * the transaction such as the API name, request ID, endpoint, and any additional application
 * state data relevant to the completed request. This allows for tracking and monitoring the
 * status and response times of API calls within the application.
 *
 * @param {string} type - The category or action type of the API transaction.
 * @param {AnyAction} action - The Redux action associated with the API call, containing metadata
 *                             such as request details and endpoint information.
 * @param {RootState} state - The application state at the time of API transaction completion.
 */
export const qalTrackApiComplete = (type: string, action: AnyAction, state: RootState): void => {
  // Destructure metadata from the action for easy access to request details
  const { requestId, baseQueryMeta, arg } = action.meta || {};
  const { endpointName } = arg;

  // Retrieve the API endpoint from the response metadata, with fallback in case of missing data
  const apiEndpoint = baseQueryMeta?.response?.url || '';

  // Construct the API name using the type and endpoint name for unique identification in analytics
  const apiType = getApiName(type);
  const apiName = `${apiType}/${endpointName}`;

  // Track the completion event with analytics, including additional context from the application state
  analytics.trackApiComplete({
    eventAction: apiName, // Combined API name and endpoint
    eventLabel: getApiStatus(type), // Status label indicating completion status
    data: {
      apiEndpoint, // Full API endpoint URL
      apiName, // Concise identifier for the API transaction
      requestId, // Unique request ID for correlating with API start events
      ...mapStore(state), // Additional state data for contextual insights
    },
  });
};

/**
 * Push qalTrackGoalEvent event to the dataLayer
 */
export const qalTrackGoalEvent = (payload: GoalEvent, store?: RootState): void => {
  const { event, eventAction, eventLabel } = payload;

  const goalPayload = {
    event,
    eventAction,
    eventLabel,
  };

  if (!store) return analytics.trackCustomEvent(goalPayload);

  /* Retrieve Quote event
  - Attach Quote Number Query Parameter in to quoteNumber Dimension
  - Only affect the retrieve_quote event.
  */
  const dataPoints = mapStore(store);
  if (event === GoalEvents.RetrieveQuote) {
    return analytics.trackCustomEvent({
      ...goalPayload,
      data: {
        ...dataPoints,
        quoteNumber: store.analytics.retrieveQuote.retrieveQuoteId || analyticsConstant.NOT_SET,
      },
    });
  }
  return analytics.trackCustomEvent({
    ...goalPayload,
    data: {
      ...dataPoints,
    },
  });
};

/**
 * Push qalTrackGoalEvent event to the dataLayer
 */
export const qalTrackEvent = (payload: AnalyticsStandard, store?: RootState): void => {
  const { data = {} } = payload;

  const dataPoints = store ? mapStore(store) : {};

  return analytics.trackCustomEvent({
    event: payload.event || AnalyticsStandardEvents.Standard,
    eventAction: payload.eventAction || analyticsConstant.NOT_SET,
    eventLabel: payload.eventLabel || analyticsConstant.NOT_SET,
    data: {
      ...data,
      ...dataPoints,
    },
  });
};
